import { useEffect, useRef } from 'react';
import { applyBackspace, applyDigitInput, applyPaste, type CodeInputResult } from '../../lib/twofa/codeInput';

interface TwoFactorCodeInputsProps {
  code: string[];
  onChange: (code: string[]) => void;
  /** Chiamata quando tutte le cifre sono inserite (digitazione, incolla o compilazione del telefono) */
  onComplete: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
}

/**
 * Le 6 caselle del codice in stile Notion. Tastiera numerica sul telefono (inputMode + pattern),
 * compilazione automatica del codice ricevuto via SMS/email (autocomplete="one-time-code"),
 * incolla di 6 cifre in qualunque casella. Logica pura in lib/twofa/codeInput.ts.
 */
export function TwoFactorCodeInputs({ code, onChange, onComplete, disabled, autoFocus = true, invalid }: TwoFactorCodeInputsProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const firstEmpty = code.findIndex((d) => !d);
    refs.current[firstEmpty >= 0 ? firstEmpty : 0]?.focus();
    // solo all'apertura e quando torna attivo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  const apply = (result: CodeInputResult) => {
    onChange(result.code);
    if (result.focus !== null) refs.current[result.focus]?.focus();
    if (result.complete) onComplete(result.complete);
  };

  return (
    <div className="grid grid-cols-6 gap-1.5 sm:gap-2 w-full max-w-[320px] mx-auto" role="group" aria-label="Codice di verifica">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { refs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Cifra ${index + 1} di ${code.length}`}
          aria-invalid={invalid || undefined}
          value={digit}
          disabled={disabled}
          onChange={(e) => apply(applyDigitInput(code, index, e.target.value))}
          onPaste={(e) => {
            e.preventDefault();
            apply(applyPaste(code, index, e.clipboardData.getData('text')));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              apply(applyBackspace(code, index));
            } else if (e.key === 'ArrowLeft' && index > 0) {
              refs.current[index - 1]?.focus();
            } else if (e.key === 'ArrowRight' && index < code.length - 1) {
              refs.current[index + 1]?.focus();
            }
          }}
          onFocus={(e) => e.target.select()}
          className={`w-full min-w-0 h-12 sm:h-14 text-center text-xl font-semibold text-[#37352F] bg-white rounded-lg border outline-none transition-colors disabled:opacity-50 focus:border-[#2383E2] focus:ring-2 focus:ring-[rgba(35,131,226,0.15)] ${
            invalid ? 'border-red-300' : 'border-[rgba(55,53,47,0.16)]'
          }`}
        />
      ))}
    </div>
  );
}
